import SearchableSelect from '../common/SearchableSelect';
import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { makeApiRequests, ABORT_ERROR } from '../../helpers/api';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ENDPOINTS } from '../../helpers/constants';


const nextActions = ['Call customer', 'Text customer', 'Schedule appointment', 'Send application'];
const moveTimelines = ['0-90 days', '3-6 months', '6+ months', 'Not sure'];

function ChoiceButtons({ options, value, onChange }) {
  return (
    <div className="d-flex">
      {options.map(option => (
        <Button
          key={option}
          type="button"
          variant={value === option ? 'primary' : 'outline-secondary'}
          className="flex-fill mr-2"
          onClick={() => onChange(value === option ? '' : option)}
        >
          {option}
        </Button>
      ))}
    </div>
  );
}

function QuickAddModal({ leadSources = [], statuses = [], onClose, onCreated, onExisting, compact = false, initialBuyer = '', salesLocation = '', storeName = '' }) {
  const [step, setStep] = useState(1);
  const [buyer, setBuyer] = useState(initialBuyer);
  const [cellPhone, setCellPhone] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [leadSource, setLeadSource] = useState('');
  const [status, setStatus] = useState(statuses.includes('New Leads') ? 'New Leads' : statuses[0] || 'New Leads');
  const [land, setLand] = useState('');
  const [homeType, setHomeType] = useState('');
  const [targetPayment, setTargetPayment] = useState('');
  const [timeline, setTimeline] = useState('0-90 days');
  const [note, setNote] = useState('');
  const [nextAction, setNextAction] = useState(nextActions[0]);
  const [followUpDate, setFollowUpDate] = useState('');
  const [dueTime, setDueTime] = useState('09:30');
  const [saving, setSaving] = useState(false);
  const [matches, setMatches] = useState([]);
  const [phoneError, setPhoneError] = useState('');
  const history = useHistory();
  const [options, setOptions] = useState({ leadSources, statuses });
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState('');
  const [retry, setRetry] = useState(0);
  const [saveError, setSaveError] = useState('');
  const [assignment, setAssignment] = useState(null);
  const [chosenStore, setChosenStore] = useState('');
  const requestId = useRef(uuid());
  const busy = useRef(false);
  const active = useRef(true);
  const saveController = useRef(null);
  useEffect(() => () => { active.current = false; if (saveController.current) saveController.current.abort(); }, []);
  useEffect(() => {
    const controller = new AbortController(); let current = true;
    setOptionsLoading(true); setOptionsError('');
    const timer = setTimeout(() => controller.abort(), 10000);
    makeApiRequests({ endpoint: '/contacts/top-producer/quick-add-options', method: 'GET', signal: controller.signal }).then(result => {
      clearTimeout(timer); if (!current) return; setOptionsLoading(false);
      if (result.error || !result.response || !Array.isArray(result.response.leadSources)) {
        setOptionsError(result.error === ABORT_ERROR ? 'Lead sources took too long to load. Try again.' : result.error || 'Could not load lead sources.');
      } else {
        setAssignment(result.response.assignment || null);
        setOptions({ leadSources: result.response.leadSources, statuses: result.response.statuses || [] });
        if (!result.response.leadSources.length) setOptionsError('No lead sources are configured. Ask an administrator to add them.');
      }
    });
    return () => { current = false; clearTimeout(timer); controller.abort(); };
  }, [retry]);
  const selectedSource = leadSource || options.leadSources.find(source => /lead/i.test(source)) || options.leadSources[0] || '';
  const statusChoices = options.statuses.length ? options.statuses : statuses;


  const effectiveStore = (salesLocation && salesLocation !== 'all' ? salesLocation : chosenStore || assignment?.defaultStoreId) || '';
  const destination = storeName || assignment?.stores.find(store => store.id === effectiveStore)?.name || 'Select a sales location';
  const phoneOk = cellPhone.replace(/\D/g, '').length >= 7;
  const canContinue = buyer.trim().length > 1 && selectedSource && phoneOk && (!assignment || !!effectiveStore);

  const continueStep = event => {
    event.preventDefault(); event.stopPropagation();
    if (!phoneOk) {
      setPhoneError('Enter a cell phone with at least 7 digits.');
      return;
    }
    setPhoneError('');
    if (canContinue) setStep(2);
  };

  const save = async event => {
    event.preventDefault(); event.stopPropagation();
    if (busy.current || !canContinue) return;
    const details = step === 2 ? [note.trim(), land && `Land: ${land}`, homeType && `Looking for: ${homeType}`,
      targetPayment.trim() && `Target payment: ${targetPayment.trim()}`, timeline && `Move timeline: ${timeline}`].filter(Boolean) : [];
    busy.current = true; setSaving(true); setSaveError('');
    const controller = new AbortController(); saveController.current = controller;
    const timer = setTimeout(() => controller.abort(), 30000);
    const { response, error } = await makeApiRequests({ endpoint: ENDPOINTS.CONTACTS_TODAY_ADD, method: 'POST', signal: controller.signal,
      requestBody: { buyer: buyer.trim(), cellPhone: cellPhone.trim(), buyerEmail: buyerEmail.trim(), leadSource: selectedSource,
        status: step === 2 ? status : 'New Leads', personalNotes: details.join('\n'),
        nextAction: step === 2 ? nextAction : '', followUpDate: step === 2 ? followUpDate : '', dueTime,
        requestId: requestId.current, ...(effectiveStore ? { salesLocation: effectiveStore } : {}) } });
    clearTimeout(timer); if (!active.current) return;
    busy.current = false; setSaving(false);
    if (error || !response) {
      setSaveError(error === ABORT_ERROR ? 'The save is taking longer than expected. Retry with this form, or search for the customer; do not start another submission.' : error || 'The save was not confirmed. Try again.');
      return;
    }
    if (!response.created) { setMatches(response.matches || []); setStep(1); return; }
    if (!response.contact?._id) { setSaveError('The save was not confirmed. Search for the customer before trying again.'); return; }
    toast.success(`${buyer.trim()} added`); onCreated(response.contact); onClose();
  };

  return (
    <Modal show onHide={() => !saving && onClose()} centered backdrop={saving ? 'static' : true} keyboard={!saving}>
      <Form onSubmit={save}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>
            <div className="text-muted small font-weight-normal">{step === 2 ? 'Optional details' : storeName}</div>
            {step === 1 ? 'Add a new customer' : 'What do we know?'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {saveError && <div className="alert alert-danger" role="alert">{saveError}</div>}
          {assignment && <div className="bg-light border rounded p-2 small mb-3">
            <div><strong>Assigned to:</strong> {assignment.name} (you)</div>
            <div><strong>Sales location:</strong> {destination}</div>
            <div>Team and rep filters do not change the owner of a new customer.</div>
            {(!salesLocation || salesLocation === 'all') && <SearchableSelect inputId="quick-add-store" aria-label="New customer sales location"
              value={effectiveStore} options={assignment.stores.map(store => ({ value: store.id, label: store.name }))}
              placeholder="Choose sales location" isDisabled={saving} isClearable={false} onChange={setChosenStore} />}
          </div>}
          <fieldset disabled={saving}>
          {step === 1 ? (
            <>
              <p className="bg-light border rounded p-2 small">
                Keep this fast. Capture enough to start the conversation. The full record can be completed later.
              </p>
              {matches.length > 0 && (
                <div className="alert alert-warning">
                  <div className="font-weight-bold mb-2">This phone or email already exists.</div>
                  {matches.map(match => (
                    <div key={match._id} className="mb-2">
                      <div>{match.buyer} · {match.status}</div>
                      <div className="small text-muted">{match.cellPhone} {match.buyerEmail}</div>
                      {match.canOpen && (
                        <Button type="button" variant="link" className="p-0" onClick={() => onExisting ? onExisting(match) : history.push(`/crm/${match._id}`)}>
                          {onExisting ? 'Use this customer' : 'Open record'}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <Form.Group controlId="quick-add-name">
                <Form.Label>Full buyer name</Form.Label>
                <Form.Control value={buyer} onChange={event => setBuyer(event.target.value)} required autoFocus />
              </Form.Group>
              <Form.Group controlId="quick-add-phone">
                <Form.Label>Cell phone</Form.Label>
                <Form.Control
                  type="tel"
                  value={cellPhone}
                  isInvalid={!!phoneError}
                  onChange={event => {
                    setCellPhone(event.target.value);
                    setPhoneError('');
                  }}
                />
                <Form.Control.Feedback type="invalid">{phoneError}</Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="quick-add-email">
                <Form.Label>Email</Form.Label>
                <Form.Control type="email" value={buyerEmail} onChange={event => setBuyerEmail(event.target.value)} placeholder="Optional" />
              </Form.Group>
              <Form.Group className="mb-0" controlId="quick-add-source">
                <Form.Label>Lead source</Form.Label>
                <Form.Control as="select" value={selectedSource} onChange={event => setLeadSource(event.target.value)} required>
                  {!selectedSource && <option value="">{optionsLoading ? 'Loading lead sources…' : 'Choose a lead source'}</option>}
                  {options.leadSources.map(source => <option key={source}>{source}</option>)}
                </Form.Control>
                {optionsLoading && <Form.Text role="status">Loading lead sources… You can enter customer details now.</Form.Text>}
                {optionsError && <div className="text-danger small" role="alert">{optionsError} <Button type="button" size="sm" variant="link" onClick={() => setRetry(n => n + 1)}>Retry</Button></div>}
                {!canContinue && <Form.Text>Enter a name, a phone number with at least 7 digits, and a lead source to save.</Form.Text>}
              </Form.Group>
            </>
          ) : (
            <>
              <p className="bg-light border rounded p-2 small mb-3">
                <strong>{buyer}</strong><br />
                {cellPhone} · {selectedSource}
              </p>
              <Form.Group>
                <Form.Label>Customer stage</Form.Label>
                <Form.Control as="select" value={status} onChange={event => setStatus(event.target.value)}>
                  {(statusChoices.length ? statusChoices : ['New Leads']).map(item => <option key={item}>{item}</option>)}
                </Form.Control>
              </Form.Group>
              <Form.Group>
                <Form.Label>Land situation</Form.Label>
                <ChoiceButtons options={['Owns land', 'Needs land']} value={land} onChange={setLand} />
              </Form.Group>
              <Form.Group>
                <Form.Label>What are they looking for?</Form.Label>
                <ChoiceButtons options={['Singlewide', 'Doublewide']} value={homeType} onChange={setHomeType} />
              </Form.Group>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Target payment</Form.Label>
                    <Form.Control value={targetPayment} onChange={event => setTargetPayment(event.target.value)} placeholder="$2,200" />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>Move timeline</Form.Label>
                    <Form.Control as="select" value={timeline} onChange={event => setTimeline(event.target.value)}>
                      {moveTimelines.map(item => <option key={item}>{item}</option>)}
                    </Form.Control>
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group>
                <Form.Label>Conversation note</Form.Label>
                <Form.Control as="textarea" rows={3} value={note} onChange={event => setNote(event.target.value)} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Next action</Form.Label>
                <Form.Control as="select" value={nextAction} onChange={event => setNextAction(event.target.value)} required>
                  {nextActions.map(item => <option key={item}>{item}</option>)}
                </Form.Control>
              </Form.Group>
              <Row>
                <Col>
                  <Form.Group className="mb-0">
                    <Form.Label>Date</Form.Label>
                    <Form.Control type="date" value={followUpDate} onChange={event => setFollowUpDate(event.target.value)} />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-0">
                    <Form.Label>Time</Form.Label>
                    <Form.Control type="time" value={dueTime} onChange={event => setDueTime(event.target.value)} />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
          </fieldset>
        </Modal.Body>
        <Modal.Footer>
          {step === 1 ? (
            <Button type="button" variant="outline-dark" disabled={saving} onClick={onClose}>Cancel</Button>
          ) : (
            <Button type="button" variant="outline-dark" disabled={saving} onClick={() => setStep(1)}>Back</Button>
          )}
          {step === 1 && !compact && <Button type="button" variant="link" disabled={!canContinue || saving} onClick={continueStep}>Add more details</Button>}
          <Button variant="success" type="submit" disabled={!canContinue || saving}>
            {saving ? 'Saving…' : compact ? 'Create and use customer' : 'Save customer'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default QuickAddModal;
